import Box from "@mui/material/Box";
import NoDataState from "../TileStates/NoDataState";
import { useLocation } from "react-router-dom";
import { useContext, useRef, useEffect } from "react";
import { ReactReduxContext } from "react-redux";
import {
  hasRecoveryAttempt,
  isChunkLoadError,
  markRecoveryAttempt,
  reloadDocument
} from "../AppVersionUpdate/utils";
import { getCurrentStoreId } from "src/components/HeaderLink/slice/header.slice";
import type { RootState } from "src/store/configureStore";
import {
  reportAutoReloadAttempt,
  reportChunkLoadError
} from "src/utils/clientEventLogger";

const GenericErrorFallback = ({ error }: { error?: unknown }) => {
  const isChunkFailure = isChunkLoadError(error);
  const currentVersion = process.env.VITE_VERSION_NUMBER ?? "";
  // Null at the root boundary, which renders outside the Redux Provider.
  const reduxContext = useContext(ReactReduxContext);

  useEffect(() => {
    if (!isChunkFailure) return;
    const storeId = reduxContext
      ? getCurrentStoreId(reduxContext.store.getState() as RootState)
      : undefined;

    reportChunkLoadError(storeId);
    if (hasRecoveryAttempt(currentVersion)) return;
    // Persist the guard first so a failed write can never start a reload loop.
    if (markRecoveryAttempt(currentVersion)) {
      reportAutoReloadAttempt(storeId);
      reloadDocument();
    }
  }, [isChunkFailure, currentVersion, reduxContext]);

  return (
    <Box sx={{ height: "100vh", width: "100vw" }}>
      <NoDataState
        text={isChunkFailure ? "appVersion.recoveryError" : "appError.generic"}
        textStyle={{ fontSize: "16px", marginTop: "16px", fontWeight: "500" }}
        {...(isChunkFailure
          ? {
              buttonText: "appVersion.refresh",
              OnReloadClick: reloadDocument
            }
          : {})}
      />
    </Box>
  );
};

const ErrorFallbackWithRouter = ({
  error,
  resetErrorBoundary
}: {
  error?: unknown;
  resetErrorBoundary: any;
}) => {
  const { pathname } = useLocation();
  const originalPathname = useRef(pathname);

  useEffect(() => {
    if (pathname !== originalPathname.current) {
      resetErrorBoundary();
    }
  }, [pathname, resetErrorBoundary]);

  return <GenericErrorFallback error={error} />;
};

export { ErrorFallbackWithRouter, GenericErrorFallback };
